/**
 * Per-case rules override + discrepancies panel ( item ).
 *
 * Shown on the case detail (Documents tab). Displays:
 *  - the active per-case master-doc override (if any) + a control for an
 *    operator to set/supersede the master doc (select a document + reason),
 *  - the list of intake discrepancies (field-value conflicts between the
 *    master doc and supporting docs).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, AlertTriangle, CheckCircle2, Save } from 'lucide-react';
import { useToastStore } from '../../stores/toast';
import { useAuthStore } from '../../stores/auth';
import { type DocumentItem } from '../../api/cases';
import {
  getCaseRulesOverride,
  setCaseRulesOverride,
  getCaseDiscrepancies,
  type RulesOverride,
  type DiscrepancyItem,
} from '../../api/rulesProfile';

function prettyField(name: string): string {
  return name.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function docLabel(doc: DocumentItem | undefined): string {
  if (!doc) return 'unknown document';
  const cat = doc.doc_category ? `[${doc.doc_category}] ` : '';
  return `${cat}${doc.original_filename}`;
}

export default function RulesOverridePanel({
  caseId,
  documents,
}: {
  caseId: string;
  documents: DocumentItem[];
}) {
  const { user } = useAuthStore();
  const { addToast } = useToastStore();
  const isOperator = user?.role === 'operator';

  const [loading, setLoading] = useState(true);
  const [override, setOverride] = useState<RulesOverride | null>(null);
  const [defaultCat, setDefaultCat] = useState<string | null>(null);
  const [discrepancies, setDiscrepancies] = useState<DiscrepancyItem[]>([]);
  const [selectedDocId, setSelectedDocId] = useState<string>('');
  const [reason, setReason] = useState<string>('');
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [ov, disc] = await Promise.all([
        getCaseRulesOverride(caseId),
        getCaseDiscrepancies(caseId),
      ]);
      setOverride(ov.override);
      setDefaultCat(ov.default_master_category);
      setDiscrepancies(disc.items);
      setSelectedDocId(ov.override?.master_document_id ?? '');
      setReason(ov.override?.reason ?? '');
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('RulesOverridePanel load failed', err);
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => { void load(); }, [load]);

  const handleSave = async () => {
    if (!selectedDocId) {
      addToast('Select a document to set as master.', 'error');
      return;
    }
    setSaving(true);
    try {
      const res = await setCaseRulesOverride(caseId, {
        master_document_id: selectedDocId,
        reason: reason.trim() || undefined,
      });
      setOverride(res.override);
      setDefaultCat(res.default_master_category);
      addToast('Master-document override saved (audited).', 'success');
    } catch (err) {
      addToast('Failed to save master-doc override.', 'error');
      // eslint-disable-next-line no-console
      console.error(err);
    } finally {
      setSaving(false);
    }
  };

  const docsById = new Map(documents.map((d) => [d.id, d]));
  const masterDoc = override ? docsById.get(override.master_document_id) : undefined;

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-slate-400 text-sm py-3">
        <Loader2 size={14} className="animate-spin" /> Loading rules override...
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Master-doc override control */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <CheckCircle2 size={15} className="text-primary-600" />
          Master Document (Rules Profile)
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Default master category: <span className="font-mono">{defaultCat ?? 'cover_page'}</span>.
          The master doc is identified by its category (not upload order). An
          operator can override which document is master for this case; every
          override is audited.
        </p>

        {override ? (
          <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-3 py-2 mb-3">
            Active override: <span className="font-medium">{docLabel(masterDoc)}</span>
            {override.reason ? <> &mdash; reason: {override.reason}</> : null}
            {override.created_at ? (
              <> &mdash; set {new Date(override.created_at).toLocaleString()}</>
            ) : null}
          </div>
        ) : (
          <div className="text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded px-3 py-2 mb-3">
            No per-case override in effect; the master doc is the document
            classified as <span className="font-mono">{defaultCat ?? 'cover_page'}</span>.
          </div>
        )}

        {isOperator ? (
          <div className="grid grid-cols-1 md:grid-cols-[1fr_2fr_auto] gap-2 items-start">
            <select
              value={selectedDocId}
              onChange={(e) => setSelectedDocId(e.target.value)}
              className="border border-slate-300 rounded px-2 py-1.5 text-sm bg-white"
              disabled={saving}
            >
              <option value="">Select document...</option>
              {documents.map((d) => (
                <option key={d.id} value={d.id}>
                  {d.doc_category ? `[${d.doc_category}] ` : ''}{d.original_filename}
                </option>
              ))}
            </select>
            <input
              type="text"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason (optional, audited)"
              className="border border-slate-300 rounded px-2 py-1.5 text-sm"
              disabled={saving}
            />
            <button
              onClick={handleSave}
              disabled={saving || !selectedDocId}
              className="btn-primary text-sm flex items-center gap-1.5 disabled:opacity-50"
            >
              {saving ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
              Save override
            </button>
          </div>
        ) : (
          <p className="text-xs text-slate-400 italic">
            Only an operator can set or change the master-document override.
          </p>
        )}
      </div>

      {/* Discrepancies list */}
      <div className="border border-slate-200 rounded-lg p-4 bg-white">
        <h3 className="text-sm font-semibold text-slate-800 mb-1 flex items-center gap-2">
          <AlertTriangle size={15} className="text-amber-600" />
          Intake Discrepancies ({discrepancies.length})
        </h3>
        <p className="text-xs text-slate-500 mb-3">
          Field-value conflicts between the master doc and supporting docs.
          The master value is used; the conflict is recorded (drives tier),
          not auto-escalated.
        </p>
        {discrepancies.length === 0 ? (
          <p className="text-xs text-slate-400 italic">No discrepancies recorded.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-slate-200 text-left">
                  <th className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Field</th>
                  <th className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Master value</th>
                  <th className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Conflicting doc</th>
                  <th className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">Conflicting value</th>
                  <th className="px-2 py-1.5 text-xs font-semibold text-slate-500 uppercase">When</th>
                </tr>
              </thead>
              <tbody>
                {discrepancies.map((d) => {
                  const conflictDoc = docsById.get(d.conflicting_document_id);
                  return (
                    <tr key={d.id} className="border-b border-slate-100">
                      <td className="px-2 py-2 text-xs font-medium text-slate-700">
                        {prettyField(d.field_name)}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-700">
                        {d.master_value ?? '-'}
                        <div className="text-[10px] text-slate-400">
                          {d.master_doc_category ?? ''}
                        </div>
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-700">
                        {docLabel(conflictDoc)}
                      </td>
                      <td className="px-2 py-2 text-xs text-amber-700">
                        {d.conflicting_value ?? '-'}
                      </td>
                      <td className="px-2 py-2 text-xs text-slate-400">
                        {d.created_at ? new Date(d.created_at).toLocaleString() : '-'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}